import { useState, useCallback, useRef } from 'react'
import { getDraft, saveDraft } from '../lib/api'

// Single linear flow: email → guidelines → name → grade → photo → peopleCheck → title → review
export type Step = 'email' | 'guidelines' | 'name' | 'grade' | 'photo' | 'peopleCheck' | 'title' | 'review'

const STEPS: Step[] = ['email', 'guidelines', 'name', 'grade', 'photo', 'peopleCheck', 'title', 'review']

export interface FormData {
  name: string
  email: string
  grade: string
  photo: File | null
  photoPreview: string | null
  photoTitle: string
  // Guidelines acceptance
  guidelinesAcceptedAt: string | null
  // People/permission - simplified to yes/no questions
  includesPeople: boolean | null
  hasModelPermission: boolean | null
  // Draft-related fields (for restoring from saved draft)
  photoS3Key?: string // S3 key if photo was already uploaded
}

export interface WizardState {
  currentStep: number
  step: Step
  totalSteps: number
  direction: 1 | -1
  formData: FormData
  goNext: () => void
  goBack: () => void
  goToStep: (step: number) => void
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  canGoBack: boolean
  isLastStep: boolean
  isFirstStep: boolean
  // Draft functionality
  hasDraft: boolean
  isDraftLoading: boolean
  loadDraftForEmail: (email: string) => Promise<boolean>
  clearDraft: () => void
}

const initialFormData: FormData = {
  name: '',
  email: '',
  grade: '',
  photo: null,
  photoPreview: null,
  photoTitle: '',
  guidelinesAcceptedAt: null,
  includesPeople: null,
  hasModelPermission: null,
}

export function useSubmissionWizard(): WizardState {
  const [currentStep, setCurrentStep] = useState(0)
  const [direction, setDirection] = useState<1 | -1>(1)
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [hasDraft, setHasDraft] = useState(false)
  const [isDraftLoading, setIsDraftLoading] = useState(false)

  // Track if we need to save draft (debounced)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedDataRef = useRef<string>('')
  // Use ref to always have latest formData in async callbacks
  const formDataRef = useRef(formData)
  formDataRef.current = formData

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < STEPS.length) {
        setDirection(step > currentStep ? 1 : -1)
        setCurrentStep(step)
      }
    },
    [currentStep]
  )

  const updateField = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  // Load draft from server
  const loadDraftForEmail = useCallback(async (email: string): Promise<boolean> => {
    setIsDraftLoading(true)
    try {
      const result = await getDraft(email)
      if (result.data?.draft) {
        const draft = result.data.draft
        setFormData((prev) => ({
          ...prev,
          email,
          name: draft.name || '',
          grade: draft.grade || '',
          photoTitle: draft.photoTitle || '',
          guidelinesAcceptedAt: draft.guidelinesAcceptedAt || null,
          includesPeople: draft.includesPeople ?? null,
          hasModelPermission: draft.hasModelPermission ?? null,
          photoS3Key: draft.photoS3Key,
          photoPreview: draft.photoUrl || null,
        }))
        setHasDraft(true)
        // Go to the step they were on (or step after email if draft exists)
        const draftStep = draft.draftStep > 0 ? draft.draftStep : 1
        setCurrentStep(draftStep)
        setDirection(1)
        return true
      }
      return false
    } catch (err) {
      console.error('Failed to load draft:', err)
      return false
    } finally {
      setIsDraftLoading(false)
    }
  }, [])

  // Clear draft state (for "start fresh" option)
  const clearDraft = useCallback(() => {
    setFormData(initialFormData)
    setHasDraft(false)
    setCurrentStep(0)
    lastSavedDataRef.current = ''
  }, [])

  // Save draft to server - uses ref to get latest formData
  const saveDraftToServer = useCallback(async (stepToSave: number) => {
    const currentFormData = formDataRef.current
    if (!currentFormData.email) return

    // Create a serializable version of form data for comparison
    const dataToSave = {
      email: currentFormData.email,
      draftStep: stepToSave,
      name: currentFormData.name || undefined,
      grade: currentFormData.grade || undefined,
      photoTitle: currentFormData.photoTitle || undefined,
      photoS3Key: currentFormData.photoS3Key || undefined,
      guidelinesAcceptedAt: currentFormData.guidelinesAcceptedAt || undefined,
      includesPeople: currentFormData.includesPeople ?? undefined,
      hasModelPermission: currentFormData.hasModelPermission ?? undefined,
    }

    const dataString = JSON.stringify(dataToSave)

    // Skip if nothing changed
    if (dataString === lastSavedDataRef.current) return

    try {
      await saveDraft(dataToSave)
      lastSavedDataRef.current = dataString
    } catch (err) {
      // Silent fail - don't interrupt user flow
      console.error('Failed to save draft:', err)
    }
  }, [])

  // Debounced save on step change (2 second delay)
  const scheduleDraftSave = useCallback(
    (newStep: number) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      // Only save after email step (step > 0)
      if (newStep > 0 && formDataRef.current.email) {
        saveTimeoutRef.current = setTimeout(() => {
          saveDraftToServer(newStep)
        }, 2000)
      }
    },
    [saveDraftToServer]
  )

  // Trigger save when navigating steps
  const goNextWithSave = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      const newStep = currentStep + 1
      setDirection(1)
      setCurrentStep(newStep)
      scheduleDraftSave(newStep)
    }
  }, [currentStep, scheduleDraftSave])

  const goBackWithSave = useCallback(() => {
    if (currentStep > 0) {
      const newStep = currentStep - 1
      setDirection(-1)
      setCurrentStep(newStep)
      scheduleDraftSave(newStep)
    }
  }, [currentStep, scheduleDraftSave])

  return {
    currentStep,
    step: STEPS[currentStep],
    totalSteps: STEPS.length,
    direction,
    formData,
    goNext: goNextWithSave,
    goBack: goBackWithSave,
    goToStep,
    updateField,
    canGoBack: currentStep > 0,
    isLastStep: currentStep === STEPS.length - 1,
    isFirstStep: currentStep === 0,
    // Draft functionality
    hasDraft,
    isDraftLoading,
    loadDraftForEmail,
    clearDraft,
  }
}
